class DynamicSiteLogo {
    constructor() {
        this.body = document.querySelector("body");
        this.header = document.querySelector("header");
        this.logo = document.querySelector("img.custom-logo");
        this.solidHeaderContainer = document.querySelector("main.solid-header__body-container");
        this.bottomNavigationHeaderEnabled = this.body && this.body.classList.contains("image-theme-bottom-navigation-header-enabled");
        this.bottomNavigationHeaderOpeningClass = "image-theme-bottom-navigation-header-opening";
        this.bottomNavigationHeaderBarClass = "bottom-navigation-header__bar";
        this.bottomNavigationHeaderDesktopQuery = window.matchMedia("(min-width: 1000px)");
        this.bottomNavigationHeaderLightClass = "image-theme-bottom-navigation-header-light-background";
        this.bottomNavigationHeaderDarkClass = "image-theme-bottom-navigation-header-dark-background";
        this.scrollEffectEnabled = this.body && this.body.classList.contains("image-theme-scroll-effect-enabled");

        // Grab current logo src before wpApiSettings overrides it — used as last-resort fallback
        const currentSrc = this.logo ? this.logo.getAttribute("src") : "";

        // Pull logo URLs from WP Customizer; fall back to light logo (always set) or current src
        const hasSettings = typeof window !== "undefined" && typeof window.wpApiSettings !== "undefined";

        if (hasSettings) {
            this.lightLogo = window.wpApiSettings.light_logo || currentSrc;
            this.darkLogo = window.wpApiSettings.dark_logo || this.lightLogo; // fall back to light if dark not set
        } else {
            // wpApiSettings unavailable — keep whatever logo is already rendered
            this.darkLogo = currentSrc;
            this.lightLogo = currentSrc;
        }

        this.init();
    }

    init() {
        if (!this.body || !this.header) {
            return;
        }

        if (this.isBottomNavigationHeaderDesktopEnabled()) {
            this.setupBottomNavigationHeaderBar();
        }

        // Force dark header on solid-background and text/wide page templates
        if (
            this.solidHeaderContainer
            || this.body.classList.contains('page-template-text')
            || this.body.classList.contains('page-template-wide')
        ) {
            this.header.classList.add("header__always-dark");
            this.showDarkLogo();
        }

        // Re-evaluate header state on scroll, resize, and full page load
        window.addEventListener("scroll", () => this.changeHeader());
        window.addEventListener("resize", () => this.changeHeader());
        window.addEventListener("load", () => this.changeHeader());

        // Rebuild or tear down bottom nav bar when viewport crosses 1000px breakpoint
        this.bottomNavigationHeaderDesktopQuery.addEventListener("change", () => {
            if (this.isBottomNavigationHeaderDesktopEnabled()) {
                this.setupBottomNavigationHeaderBar();
            } else {
                this.body.classList.remove(this.bottomNavigationHeaderOpeningClass);
                this.clearBottomNavigationHeaderContrast();
                this.teardownBottomNavigationHeaderBar();
            }

            this.changeHeader();
        });

        // Set correct initial state based on scroll position at page load
        this.changeHeader();
    }

    isBottomNavigationHeaderDesktopEnabled() {
        return this.bottomNavigationHeaderEnabled && this.bottomNavigationHeaderDesktopQuery.matches;
    }

    setupBottomNavigationHeaderBar() {
        const headerContainer = this.header.querySelector(".header__container");
        const navigationColumn = this.header.querySelector(".header__right-column");
        const phoneNumber = this.header.querySelector(".phone-number");

        if (!headerContainer || (!navigationColumn && !phoneNumber)) {
            return;
        }

        // Create the bottom bar once and move nav/phone into it
        let bottomBar = this.header.querySelector(`.${this.bottomNavigationHeaderBarClass}`);

        if (!bottomBar) {
            bottomBar = document.createElement("div");
            bottomBar.className = this.bottomNavigationHeaderBarClass;
            headerContainer.appendChild(bottomBar);
        }

        if (navigationColumn && navigationColumn.parentElement !== bottomBar) {
            bottomBar.appendChild(navigationColumn);
        }

        if (phoneNumber && phoneNumber.parentElement !== bottomBar) {
            bottomBar.appendChild(phoneNumber);
        }
    }

    teardownBottomNavigationHeaderBar() {
        const headerContainer = this.header.querySelector(".header__container");
        const bottomBar = this.header.querySelector(`.${this.bottomNavigationHeaderBarClass}`);

        if (!headerContainer || !bottomBar) {
            return;
        }

        // Return nav/phone to header container before removing the bar
        const navigationColumn = bottomBar.querySelector(".header__right-column");
        const phoneNumber = bottomBar.querySelector(".phone-number");

        if (navigationColumn) {
            headerContainer.appendChild(navigationColumn);
        }

        if (phoneNumber) {
            headerContainer.appendChild(phoneNumber);
        }

        bottomBar.remove();
    }

    changeHeader() {
        this.scrollPos = window.scrollY;

        // During bottom nav opening phase, keep header transparent with light logo
        if (this.isBottomNavigationHeaderOpening()) {
            this.header.classList.remove("header__sticky");
            this.showLightLogo();
            this.updateBottomNavigationHeaderContrast();
            return;
        }

        // Stick and darken header after 200px scroll; revert when back at top
        if (this.scrollPos > 200) {
            this.header.classList.add("header__sticky");
            if (this.scrollEffectEnabled) {
                this.showLightLogo();
            } else {
                this.showDarkLogo();
            }
        } else {
            this.header.classList.remove("header__sticky");
            this.showLightLogo();
        }

        this.updateBottomNavigationHeaderContrast();
    }

    isBottomNavigationHeaderOpening() {
        if (!this.isBottomNavigationHeaderDesktopEnabled() || this.solidHeaderContainer) {
            this.body.classList.remove(this.bottomNavigationHeaderOpeningClass);
            return false;
        }

        const contentSections = this.getContentSections();
        const firstSection = contentSections[0] || null;

        if (!firstSection) {
            this.body.classList.remove(this.bottomNavigationHeaderOpeningClass);
            return false;
        }

        const threshold = this.getBottomNavigationHeaderOpeningThreshold(contentSections);
        const isOpening = window.scrollY < threshold - 1;

        this.body.classList.toggle(this.bottomNavigationHeaderOpeningClass, isOpening);

        return isOpening;
    }

    getBottomNavigationHeaderOpeningThreshold(contentSections) {
        const firstSection = contentSections[0];
        const secondSection = contentSections[1] || null;

        // Use top of second section as threshold; fall back to end of first section or viewport height
        if (secondSection) {
            return this.getElementDocumentTop(secondSection);
        }

        return Math.max(
            this.getElementDocumentTop(firstSection) + Math.min(firstSection.scrollHeight, window.innerHeight),
            window.innerHeight
        );
    }

    // Calculates element's absolute top offset relative to the document
    getElementDocumentTop(element) {
        let top = 0;
        let current = element;

        while (current) {
            top += current.offsetTop || 0;
            current = current.offsetParent;
        }

        return top;
    }

    // Returns visible, non-empty block-level children of .entry-content
    getContentSections() {
        const content = document.querySelector(".entry-content");

        if (!content) {
            return [];
        }

        return Array.from(content.children).filter((child) => {
            if (child.matches("script, style")) {
                return false;
            }

            if (child.matches("p") && child.textContent.trim() === "") {
                return false;
            }

            if (child.classList.contains("wp-block-spacer")) {
                return false;
            }

            return child.getBoundingClientRect().height > 0;
        });
    }

    updateBottomNavigationHeaderContrast() {
        if (!this.isBottomNavigationHeaderDesktopEnabled()) {
            this.clearBottomNavigationHeaderContrast();
            return;
        }

        const bottomBar = this.header.querySelector(`.${this.bottomNavigationHeaderBarClass}`);

        if (!bottomBar) {
            this.clearBottomNavigationHeaderContrast();
            return;
        }

        if (this.scrollEffectEnabled) {
            this.setBottomNavigationHeaderContrast("dark");
            return;
        }

        // Sample background luminance behind the bar; default to light if undetectable
        const luminance = this.getBackgroundLuminanceBehindBottomBar(bottomBar);

        if (luminance === null) {
            this.setBottomNavigationHeaderContrast("light");
            return;
        }

        this.setBottomNavigationHeaderContrast(luminance > 0.5 ? "light" : "dark");
    }

    // Samples luminance at 5 horizontal points across the bottom bar's midline
    getBackgroundLuminanceBehindBottomBar(bottomBar) {
        const rect = bottomBar.getBoundingClientRect();
        const y = Math.min(window.innerHeight - 1, Math.max(0, rect.top + rect.height / 2));
        const samplePoints = [
            rect.left + rect.width * 0.1,
            rect.left + rect.width * 0.3,
            rect.left + rect.width / 2,
            rect.left + rect.width * 0.7,
            rect.left + rect.width * 0.9,
        ].map((x) => Math.min(window.innerWidth - 1, Math.max(0, x)));
        const luminanceSamples = [];

        for (const x of samplePoints) {
            const color = this.getBackgroundColorAtPoint(x, y);

            if (color) {
                luminanceSamples.push(this.getRelativeLuminance(color));
            }
        }

        if (!luminanceSamples.length) {
            return null;
        }

        // Return median luminance to avoid skewing from outlier colours
        luminanceSamples.sort((a, b) => a - b);

        return luminanceSamples[Math.floor(luminanceSamples.length / 2)];
    }

    // Walks elements at (x, y), skipping header elements, to find first background colour
    getBackgroundColorAtPoint(x, y) {
        const elements = document.elementsFromPoint(x, y);

        for (const element of elements) {
            if (this.header.contains(element)) {
                continue;
            }

            const color = this.findElementBackgroundColor(element);

            if (color) {
                return color;
            }
        }

        return null;
    }

    // Traverses DOM upward checking for media, cover block, scroll-effect, or CSS background colour
    findElementBackgroundColor(element) {
        let current = element;

        while (current && current !== document.documentElement) {
            const mediaColor = this.getMediaBackgroundColor(current);

            if (mediaColor) {
                return mediaColor;
            }

            const coverColor = this.getCoverBackgroundColor(current);

            if (coverColor) {
                return coverColor;
            }

            const sectionColor = this.getSectionEffectBackgroundColor(current);

            if (sectionColor) {
                return sectionColor;
            }

            const style = window.getComputedStyle(current);
            const color = this.parseCssColor(style.backgroundColor);

            if (color && color.a > 0) {
                return color;
            }

            current = current.parentElement;
        }

        return null;
    }

    // Treats any img/video or carousel containing media as effectively black background
    getMediaBackgroundColor(element) {
        const mediaElement = element.closest && element.closest("img, video, .wp-block-image, .wp-block-gallery, .glide__slide");

        if (
            mediaElement
            && (
                mediaElement.matches("img, video")
                || mediaElement.querySelector("img, video")
            )
        ) {
            return { r: 0, g: 0, b: 0, a: 1 };
        }

        const mediaSection = element.closest && element.closest(".home-carousel, .carousel-hero-caption, .glide");

        if (mediaSection && mediaSection.querySelector("img, video")) {
            return { r: 0, g: 0, b: 0, a: 1 };
        }

        return null;
    }

    // Reads custom CSS property set by the scroll-effect block for its background colour
    getSectionEffectBackgroundColor(element) {
        const section = element.closest && element.closest(".image-theme-scroll-effect__section");

        if (!section) {
            return null;
        }

        const color = this.parseCssColor(
            window.getComputedStyle(section).getPropertyValue("--image-theme-scroll-section-background").trim()
        );

        return color && color.a > 0 ? color : null;
    }

    // Resolves cover block colour from background, overlay, or media fallback
    getCoverBackgroundColor(element) {
        const cover = element.closest && element.closest(".wp-block-cover");

        if (!cover) {
            return null;
        }

        const coverStyle = window.getComputedStyle(cover);
        const coverColor = this.parseCssColor(coverStyle.backgroundColor);

        if (coverColor && coverColor.a > 0) {
            return coverColor;
        }

        const overlay = cover.querySelector(".wp-block-cover__background");

        if (overlay) {
            const overlayColor = this.parseCssColor(window.getComputedStyle(overlay).backgroundColor);

            if (overlayColor && overlayColor.a > 0) {
                return overlayColor;
            }
        }

        // Cover has a media background — treat as dark
        if (cover.querySelector(".wp-block-cover__image-background, .wp-block-cover__video-background")) {
            return { r: 0, g: 0, b: 0, a: 1 };
        }

        return null;
    }

    // Parses hex or rgb/rgba CSS colour strings into {r, g, b, a} object
    parseCssColor(color) {
        if (!color) {
            return null;
        }

        const normalizedColor = color.trim();

        if (normalizedColor.indexOf("#") === 0) {
            return this.parseHexColor(normalizedColor);
        }

        const match = normalizedColor.match(/rgba?\(([^)]+)\)/);

        if (!match) {
            return null;
        }

        const values = match[1]
            .replace(/\//g, " ")
            .split(/[\s,]+/)
            .filter(Boolean)
            .map((value) => Number.parseFloat(value.trim()));

        return {
            r: values[0],
            g: values[1],
            b: values[2],
            a: values.length > 3 ? values[3] : 1,
        };
    }

    // Expands shorthand hex (#abc → #aabbcc) then converts to {r, g, b, a}
    parseHexColor(color) {
        const hex = color.replace("#", "");

        if (![3, 6].includes(hex.length)) {
            return null;
        }

        const expandedHex = hex.length === 3
            ? hex.split("").map((character) => character + character).join("")
            : hex;
        const value = Number.parseInt(expandedHex, 16);

        if (Number.isNaN(value)) {
            return null;
        }

        return {
            r: (value >> 16) & 255,
            g: (value >> 8) & 255,
            b: value & 255,
            a: 1,
        };
    }

    // WCAG relative luminance formula — output range 0 (black) to 1 (white)
    getRelativeLuminance({ r, g, b }) {
        const [red, green, blue] = [r, g, b].map((value) => {
            const channel = value / 255;

            return channel <= 0.03928
                ? channel / 12.92
                : Math.pow((channel + 0.055) / 1.055, 2.4);
        });

        return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    }

    // Toggles light/dark contrast classes on body based on detected background tone
    setBottomNavigationHeaderContrast(backgroundTone) {
        const isLightBackground = backgroundTone === "light";

        this.body.classList.toggle(this.bottomNavigationHeaderLightClass, isLightBackground);
        this.body.classList.toggle(this.bottomNavigationHeaderDarkClass, !isLightBackground);
    }

    clearBottomNavigationHeaderContrast() {
        this.body.classList.remove(this.bottomNavigationHeaderLightClass);
        this.body.classList.remove(this.bottomNavigationHeaderDarkClass);
    }

    showDarkLogo() {
        if (!this.logo || !this.darkLogo) {
            return;
        }

        // Only swap src if it has actually changed — avoids unnecessary repaints
        if (!this.logo.getAttribute('src').includes(this.darkLogo)) {
            this.logo.srcset = '';
            this.logo.src = this.darkLogo;
        }

        this.header.classList.remove("header__light");
        this.header.classList.add("header__dark");
    }

    showLightLogo() {
        if (!this.logo || !this.lightLogo) {
            return;
        }

        // Skip swap on pages locked to dark header
        if (this.header.classList.contains('header__always-dark')) return;

        if (!this.logo.getAttribute('src').includes(this.lightLogo)) {
            this.logo.srcset = '';
            this.logo.src = this.lightLogo;
        }

        this.header.classList.remove("header__dark");
        this.header.classList.add("header__light");
    }
}

export default DynamicSiteLogo;
